/**
 * Three MM / DD / YYYY inputs, not a date picker. A native picker opens on the
 * current month and takes ~200 taps to reach 1994 on mobile; typing three
 * numbers is faster and has no locale ambiguity (anchor decision).
 *
 * Emits the assembled date as unix ms parsed in America/New_York, the zone
 * every other calendar calculation here uses, or null while incomplete or
 * invalid. Age math stays with the caller so each surface can word its own
 * refusal.
 */
import {ReactElement, useEffect, useState} from "react";
import {DateTime} from "luxon";

export const DOB_ZONE = "America/New_York";
export const MINIMUM_AGE = 18;

export interface ParsedDob {
    millis: number;
    age: number;
    plausible: boolean;
}

export function parseDob(month: string, day: string, year: string): ParsedDob | null {
    if (month.length < 1 || day.length < 1 || year.length !== 4) {
        return null;
    }
    const dt = DateTime.fromObject({year: Number(year), month: Number(month), day: Number(day)}, {zone: DOB_ZONE});
    if (!dt.isValid) {
        return null;
    }
    const age = Math.floor(DateTime.now().setZone(DOB_ZONE).diff(dt, "years").years);
    return {millis: dt.toMillis(), age, plausible: age >= 0 && age <= 120};
}

export interface DobParts {
    month: string;
    day: string;
    year: string;
}

interface Props {
    idPrefix: string;
    onChange: (parsed: ParsedDob | null) => void;
    /** Controlled form: pass both to keep the typed strings across a remount (the modal's confirm step). */
    value?: DobParts;
    onPartsChange?: (parts: DobParts) => void;
    invalid?: boolean;
    disabled?: boolean;
    className?: string;
}

export function DateOfBirthInputs({idPrefix, onChange, value, onPartsChange, invalid, disabled, className}: Props): ReactElement {
    const [local, setLocal] = useState<DobParts>({month: "", day: "", year: ""});
    const parts = value ?? local;
    const {month, day, year} = parts;
    const setParts = (next: DobParts) => {
        setLocal(next);
        onPartsChange?.(next);
    };
    const setMonth = (v: string) => setParts({...parts, month: v});
    const setDay = (v: string) => setParts({...parts, day: v});
    const setYear = (v: string) => setParts({...parts, year: v});

    useEffect(() => {
        onChange(parseDob(month, day, year));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [month, day, year]);

    const digits = (v: string, max: number) => v.replace(/\D/g, "").slice(0, max);

    return (
        <div className={className}>
            <input
                id={`${idPrefix}-month`}
                inputMode="numeric"
                placeholder="MM"
                aria-label="Month"
                maxLength={2}
                value={month}
                disabled={disabled}
                aria-invalid={invalid}
                onChange={(e) => setMonth(digits(e.target.value, 2))}
            />
            <input
                id={`${idPrefix}-day`}
                inputMode="numeric"
                placeholder="DD"
                aria-label="Day"
                maxLength={2}
                value={day}
                disabled={disabled}
                aria-invalid={invalid}
                onChange={(e) => setDay(digits(e.target.value, 2))}
            />
            <input
                id={`${idPrefix}-year`}
                inputMode="numeric"
                placeholder="YYYY"
                aria-label="Year"
                maxLength={4}
                value={year}
                disabled={disabled}
                aria-invalid={invalid}
                onChange={(e) => setYear(digits(e.target.value, 4))}
            />
        </div>
    );
}

export default DateOfBirthInputs;
